import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchProduct, imgUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSite } from "@/context/SiteContext";
import Turntable from "@/components/product/Turntable";
import HotspotBubble from "@/components/product/HotspotBubble";
import MotifMagic from "@/components/product/MotifMagic";
import { Motif } from "@/lib/motifs";
import SeoHead from "@/components/site/SeoHead";
import { toast } from "sonner";

const Product = ({ legacySlug }) => {
  const params = useParams();
  const slug = legacySlug || params.slug;
  const [p, setP] = useState(null);
  const [size, setSize] = useState(null);
  const [tab, setTab] = useState("story");
  const [pair, setPair] = useState(false);
  const { add } = useCart();
  const { location } = useSite();

  useEffect(() => {
    fetchProduct(slug).then((data) => {
      setP(data);
      const sizes = data.sizes || [];
      setSize(sizes.length ? sizes[0] : "one-size");
    });
    window.scrollTo(0, 0);
  }, [slug]);

  const hotspots = useMemo(() => p?.hotspots || [], [p]);

  if (!p) {
    return <div className="pt-40 min-h-screen max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 text-sm text-ink/60" data-testid="product-loading">Loading…</div>;
  }

  const pairPartner = p.pair_partner;
  const displayPrice = pair && p.pair_price ? p.pair_price : p.price;
  const heroImg = (p.images || [{}])[0].url;

  const onAdd = () => {
    const chosen = size || "one-size";
    const available = (p.stock || {})[chosen] ?? 0;
    if ((p.sizes || []).length > 0 && !size) return toast.error("Choose a size first");
    if (available <= 0) return toast.error("This size just sold out");
    add(p, chosen, { isPair: pair });
    toast.success(`${p.name} added · ${available - 1} left`);
  };

  const stockFor = (s) => (p.stock || {})[s] ?? 0;
  const currentStock = stockFor(size || "one-size");
  const totalStock = Object.values(p.stock || {}).reduce((a, b) => a + (b || 0), 0);

  return (
    <div className="pt-24 md:pt-28" data-testid="product-page">
      <SeoHead
        title={p.name}
        description={
          p.hook ||
          `${p.name} — hand-embroidered ${p.motif || ""} piece in ${p.category || "MELAVI"}'s slow-made collection. ₹${p.price?.toLocaleString?.("en-IN")}.`
        }
        path={`/product/${p.slug}`}
        image={p.images?.[0]?.url}
        type="product"
        price={p.price}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: p.name,
          image: [
            p.images?.[0]?.url
              ? (p.images[0].url.startsWith("http") ? p.images[0].url : `https://melavi.in${p.images[0].url}`)
              : "https://melavi.in/og-default.jpg",
          ],
          description: p.hook || p.story || "",
          sku: p.slug,
          brand: { "@type": "Brand", name: "MELAVI" },
          offers: {
            "@type": "Offer",
            url: `https://melavi.in/product/${p.slug}`,
            priceCurrency: "INR",
            price: p.price,
            availability:
              totalStock > 0
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock",
          },
        }}
      />
      {/* Hero: turntable on a plinth */}
      <section className="relative">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 pt-16 md:pt-24">
          <Link to="/shop" className="nav-item text-ink/60 hover:text-ink" data-testid="product-back">← Back to shop</Link>
        </div>

        <div className="relative max-w-[1440px] mx-auto px-4 md:px-12 lg:px-24 pt-8 pb-24 grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Plinth */}
          <div className="lg:col-span-7 relative">
            {/* Outer wrapper holds hotspots (overflow-visible so their rings/panes can spill).
                Inner wrapper clips the backdrop/plinth/grain (overflow-hidden). */}
            <div className="relative w-full aspect-[4/5]">
              <div className="absolute inset-0 overflow-hidden" style={{ background: "var(--sand)" }}>
                {/* Backdrop */}
                <img
                  src={location.image}
                  alt=""
                  aria-hidden
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ opacity: 0.35, filter: "blur(2px) saturate(0.85)" }}
                />
                {/* Plinth shape */}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[80%] h-8" style={{ background: "linear-gradient(180deg, rgba(28,26,25,0) 0%, rgba(28,26,25,0.18) 100%)" }} />
                <Turntable frames={p.turntable_frames} alt={p.name} />
                <div className="grain absolute inset-0" />
              </div>
              {/* Hotspots live above the clipped inner wrapper so rings & popovers don't get cut */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="relative w-full h-full pointer-events-auto">
                  <MotifMagic motifKey={p.motif} />
                  {hotspots.map((h, i) => <HotspotBubble key={h.key || i} hotspot={h} index={i} />)}
                </div>
              </div>
            </div>

            {/* thumbnail strip */}
            <div className="mt-6 flex gap-3 overflow-x-auto no-scrollbar" data-testid="product-thumbs">
              {(p.images || []).slice(0, 6).map((im, i) => (
                <div key={i} className="flex-none w-16 h-20 hairline overflow-hidden">
                  <img src={imgUrl(im.url)} alt={im.role} className="w-full h-full object-cover" loading="lazy" />
                </div>
              ))}
            </div>
          </div>

          {/* Info column */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <Motif name={p.motif} size={24} strokeColor="var(--accent)" />
              <span className="nav-item text-ink/60">{p.category}</span>
            </div>
            <h1 className="serif text-5xl md:text-6xl leading-[1.05]" data-testid="product-name">{p.name}</h1>
            <p className="mt-4 text-ink/80 leading-relaxed">{p.hook}</p>

            <div className="mt-8 flex items-baseline gap-4">
              <span className="serif text-3xl" data-testid="product-price">₹{displayPrice.toLocaleString("en-IN")}</span>
              {pair && p.price && (
                <span className="text-sm text-ink/60 line-through">₹{(p.price + (pairPartner?.price || 0)).toLocaleString("en-IN")}</span>
              )}
              {p.age_range && <span className="text-sm text-ink/70">· {p.age_range}</span>}
            </div>

            {/* Pair toggle */}
            {(p.pair_slug || pairPartner) && (
              <div className="mt-6 hairline p-4" data-testid="pair-toggle">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pair}
                    onChange={(e) => setPair(e.target.checked)}
                    className="mt-1 accent-[#AE2B31]"
                    data-testid="pair-checkbox"
                  />
                  <span className="text-sm">
                    <span className="serif text-lg block">Buy as a twinning pair</span>
                    <span className="text-ink/70 text-xs block mt-1">
                      With {(pairPartner?.name) || (p.pair_slug || "").replace(/-/g, " ")} · saves ₹200
                    </span>
                  </span>
                </label>
              </div>
            )}

            {/* Sizes — hidden entirely when the piece is one-size */}
            {(p.sizes || []).length > 0 && (
              <div className="mt-8" data-testid="size-select">
                <div className="flex items-baseline justify-between mb-3">
                  <div className="nav-item">Size</div>
                  {size && (
                    <div
                      className="text-xs tracking-widest uppercase"
                      style={{ color: currentStock <= 3 && currentStock > 0 ? "#AE2B31" : "var(--stone)" }}
                      data-testid="product-stock-hint"
                    >
                      {currentStock === 0
                        ? "Sold out"
                        : currentStock <= 3
                          ? `Only ${currentStock} left`
                          : `${currentStock} in stock`}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-3">
                  {(p.sizes || []).map((s) => {
                    const left = stockFor(s);
                    const soldOut = left === 0;
                    return (
                      <button
                        key={s}
                        disabled={soldOut}
                        onClick={() => !soldOut && setSize(s)}
                        className={`px-4 py-2 hairline rounded-none text-sm transition-colors relative ${
                          size === s ? "bg-ink text-ground border-ink" : "hover:border-ink"
                        } ${soldOut ? "opacity-40 line-through cursor-not-allowed hover:border-current" : ""}`}
                        data-testid={`size-${s}`}
                        title={soldOut ? "Sold out" : `${left} left`}
                      >
                        <span>{s}</span>
                        <span
                          className="ml-2 text-[10px] tracking-widest uppercase opacity-70"
                          data-testid={`size-${s}-stock`}
                        >
                          {soldOut ? "· sold" : `· ${left} left`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* One-size stock hint (age-only pieces like Dearmy hearts) */}
            {(p.sizes || []).length === 0 && (
              <div className="mt-6 text-xs tracking-widest uppercase" style={{ color: stockFor("one-size") <= 3 && stockFor("one-size") > 0 ? "#AE2B31" : "var(--stone)" }} data-testid="product-stock-onesize">
                {stockFor("one-size") === 0
                  ? "Sold out for now"
                  : stockFor("one-size") <= 3
                    ? `Only ${stockFor("one-size")} left`
                    : `${stockFor("one-size")} in stock`}
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-4">
              <button
                onClick={onAdd}
                disabled={totalStock === 0}
                className={`btn-solid ${totalStock === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                data-testid="add-to-cart-btn"
              >
                {totalStock === 0 ? "Sold out" : "Add to cart"}
              </button>
              <button
                onClick={() => { onAdd(); setTimeout(() => (window.location.href = "/checkout"), 200); }}
                disabled={totalStock === 0}
                className={`btn-ink ${totalStock === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                data-testid="buy-now-btn"
              >
                Buy it now
              </button>
            </div>

            {/* Tabs */}
            <div className="mt-14 hairline-t pt-8" data-testid="product-tabs">
              <div className="flex gap-8 mb-6">
                {[
                  { k: "story", l: "The Story" },
                  { k: "craft", l: "The Craft" },
                  { k: "care", l: "Care" },
                ].map((t) => (
                  <button
                    key={t.k}
                    onClick={() => setTab(t.k)}
                    className={`nav-item pb-2 border-b-2 transition-colors ${tab === t.k ? "border-accent text-accent" : "border-transparent text-ink/60 hover:text-ink"}`}
                    data-testid={`tab-${t.k}`}
                  >
                    {t.l}
                  </button>
                ))}
              </div>
              <div className="text-ink/80 leading-relaxed text-base">
                {tab === "story" && <p data-testid="tab-content-story">{p.story}</p>}
                {tab === "craft" && <p data-testid="tab-content-craft">{p.craft}</p>}
                {tab === "care" && <p data-testid="tab-content-care">{p.care}</p>}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Companion band: pair partner */}
      {pairPartner && (
        <section className="hairline-t py-24" data-testid="pair-band">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="nav-item mb-3">Its twin</div>
              <h2 className="serif text-4xl md:text-5xl">{pairPartner.name}</h2>
              <p className="mt-4 text-ink/80 leading-relaxed max-w-[48ch]">{pairPartner.hook}</p>
              <div className="mt-6 flex items-center gap-6">
                <span className="serif text-2xl">₹{pairPartner.price.toLocaleString("en-IN")}</span>
                <Link to={`/product/${pairPartner.slug}`} className="crimson-link nav-item !text-[12px]" data-testid="pair-link">Open its page →</Link>
              </div>
            </div>
            <img src={imgUrl((pairPartner.images || [{}])[0].url)} alt={pairPartner.name} className="w-full aspect-[4/5] object-cover" />
          </div>
        </section>
      )}

      {/* Detail images grid — the shots not on the plinth */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 py-24" data-testid="detail-grid">
        <h3 className="serif text-3xl md:text-4xl mb-10">The details, up close.</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {(p.images || []).slice(1).map((im, i) => (
            <figure key={i} className={`overflow-hidden ${i % 3 === 1 ? "md:mt-10" : ""}`}>
              <img src={imgUrl(im.url)} alt={im.role} className="w-full aspect-square object-cover" loading="lazy" />
              {im.caption && <figcaption className="mt-2 text-xs text-ink/60">{im.caption}</figcaption>}
            </figure>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Product;
